/**
 * Shared menu content rendered both inside the desktop Sidebar and the
 * mobile UserMenu drawer (HORO-10326).
 *
 * Items:
 *   - "Start a new chat"
 *   - "Memory" — gated by MEMORY_ENABLED (currently false)
 *   - "Chat history"
 *   - "Veda Plans" — the /plans Free vs Premium comparison
 *   - Advisors section: rows from useAdvisorList (anon → fixed top-5;
 *     logged-in → recents, up to 6; fewer on short screens)
 *   - "View all" — navigates to /catalog (full advisor browse)
 *   - Footer: user pill (registered) or Login button (anonymous)
 *
 * The component is purely presentational — it dispatches Redux actions
 * directly for the canonical flows (start new chat, set default advisor,
 * navigate to chat history, open login) so callers don't have to wire
 * each handler. Pass `onAfterAction` to let the host close itself
 * (drawer dismissal, etc.) after the user picks something.
 */
import classNames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import {
    APP_URL_CATALOG,
    APP_URL_CHAT,
    APP_URL_CHAT_HISTORY,
    APP_URL_LIVE_READINGS,
    APP_URL_MEMORY,
    APP_URL_PLANS,
    APP_URL_PROFILE,
    APP_URL_PROFILE_BIRTH_CHART,
    LIVE_READINGS_ENABLED,
    MEMORY_ENABLED,
    MEMORY_NEW_BADGE_UNTIL,
    STORAGE_KEY_PENDING_ADVISOR_ALIAS
} from '../../../const';
import useFeatureAnnouncement from '../../../hooks/useFeatureAnnouncement';
import { useZaraTrial } from '../../../hooks/useZaraTrial';
import { FEATURES } from '../../../service/FeatureAnnouncementService';
import {
    ADVISOR_LOCK_REASON,
    ZARA_ADVISOR_ALIAS,
    getAdvisorLockReason,
    isPremiumSubscriber,
    shouldShowZaraWelcome
} from '../../../service/advisorAccess';
import {
    getDefaultAdvisorAlias,
    getHasBirthChartReport,
    getSeenAdvisorIntros,
    getUser,
    logOut,
    toggleAuthModal
} from '../../../store/slices/authSlice';
import { BIRTH_CHART_VIEW_SURFACE, FLOW_SOURCE, openBirthChartFlow } from '../../../store/slices/birthChartSlice';
import { fetchAdvisors, getAdvisorsState } from '../../../store/slices/catalogSlice';
import { selectBirthChartEnabled } from '../../../store/slices/featuresSlice';
import { getHasUnsavedChanges, getIsRegionRestricted, setPendingNavigation } from '../../../store/slices/memorySlice';
import { openPaymentModal, setShowZaraUpsellModal, setShowZaraWelcomeModal } from '../../../store/slices/modalsSlice';
import { gaEvents } from '../../../utils/analytics';
import BirthChartIcon from '../../icons/UserMenu/BirthChartIcon';
import ChevronDownIcon from '../../icons/UserMenu/ChevronDownIcon';
import ChevronRightIcon from '../../icons/UserMenu/ChevronRightIcon';
import ClockIcon from '../../icons/UserMenu/ClockIcon';
import DocumentIcon from '../../icons/UserMenu/DocumentIcon';
import LoginIcon from '../../icons/UserMenu/LoginIcon';
import LogoutIcon from '../../icons/UserMenu/LogoutIcon';
import PlansIcon from '../../icons/UserMenu/PlansIcon';
import PlusIcon from '../../icons/UserMenu/PlusIcon';
import VideoIcon from '../../icons/UserMenu/VideoIcon';
import AdvisorRow from './AdvisorRow';
import { pushRecentAdvisorAlias } from './recentAdvisorsStorage';
import styles from './styles.module.scss';
import { useAdvisorList } from './useAdvisorList';

// Caps the visible advisor rows at 5 for both anon and logged-in — kept
// consistent with the fixed top-5 anon roster / homepage carousel. The
// fit-to-height logic below trims further when the menu container is too short.
const MAX_VISIBLE_ADVISORS = 5;
// Fallback row height used before any row is rendered (padding 14*2 +
// 40px avatar). Once a row mounts, we measure its real `offsetHeight`
// so we don't drift if padding/avatar size changes upstream.
const ADVISOR_ROW_FALLBACK_HEIGHT = 68;
// Matches `.advisorList { gap: 2px }`. Captured here so the fit math
// reads as "N rows + (N-1) gaps fit in `available`".
const ADVISOR_ROW_GAP = 2;

const MenuContent = ({ onAfterAction }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isNew: isBirthChartNew } = useFeatureAnnouncement(FEATURES.BIRTH_CHART);
    const user = useSelector(getUser);
    const isUser = !!user?.id;
    const { isExhausted: zaraTrialExhausted } = useZaraTrial();
    const seenAdvisorIntros = useSelector(getSeenAdvisorIntros);
    const isRegionRestricted = useSelector(getIsRegionRestricted);
    const hasUnsavedChanges = useSelector(getHasUnsavedChanges);
    const defaultAdvisorAlias = useSelector(getDefaultAdvisorAlias);
    const birthChartEnabled = useSelector(selectBirthChartEnabled);
    const hasBirthChartReport = useSelector(getHasBirthChartReport);
    const { advisorsList, currentRequestId } = useSelector(getAdvisorsState);
    const advisors = useAdvisorList();

    // Fetch the catalogue once when the menu first renders if it hasn't
    // been loaded yet — the advisor rows look much better with real
    // avatars + expertise labels from the backend than the static fallbacks.
    // `currentRequestId` is null until the first fetch fires, so the
    // condition also avoids dog-piling if the menu remounts while a
    // fetch is in flight.
    useEffect(() => {
        if (advisorsList.length === 0 && !currentRequestId) {
            // `categories` must start the query string ('?') — the API
            // method concatenates it before `&page=...`, so an empty
            // string produces a malformed URL.
            dispatch(fetchAdvisors({ categories: '?', page: 1, reloading: true }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fit-to-height advisor row count. The desktop sidebar and the mobile
    // drawer both have bounded heights (`100vh - header` and the drawer's
    // `.inner` respectively); when the viewport shrinks, the advisor list
    // is the only section that should give up space so the footer's Login
    // / user pill stays in view without ever needing a scrollbar.
    //
    // The `.advisorList` is rendered at its natural height so "View all"
    // sits right under the last row. The fit math compares the visual
    // bottom of `.viewAll` against the bottom of `.advisorsSection` (the
    // box the flex algorithm allotted to this region) and converts the
    // overshoot / spare-space delta into a row-count adjustment. Two
    // passes converge: render -> measure -> setState -> re-render once.
    const sectionRef = useRef(null);
    const advisorListRef = useRef(null);
    const viewAllRef = useRef(null);
    const [visibleAdvisorCount, setVisibleAdvisorCount] = useState(MAX_VISIBLE_ADVISORS);

    const recomputeAdvisorCount = useCallback(() => {
        const section = sectionRef.current;
        const viewAll = viewAllRef.current;
        if (!section || !viewAll) return;
        // Row height is measured off the first rendered AdvisorRow so the
        // math survives future padding / avatar size tweaks; fallback is
        // only used before any row has mounted.
        const firstRow = advisorListRef.current?.firstElementChild;
        const rowHeight = firstRow?.offsetHeight || ADVISOR_ROW_FALLBACK_HEIGHT;
        if (rowHeight <= 0) return;
        const slotSize = rowHeight + ADVISOR_ROW_GAP;
        const sectionRect = section.getBoundingClientRect();
        const viewAllRect = viewAll.getBoundingClientRect();
        // Positive when there's room for more rows, negative when the
        // rendered rows have pushed `.viewAll` below the section's box.
        const spare = sectionRect.bottom - viewAllRect.bottom;
        const delta = Math.floor(spare / slotSize);
        if (delta === 0) return;
        setVisibleAdvisorCount((prev) => {
            const next = Math.max(1, Math.min(MAX_VISIBLE_ADVISORS, prev + delta));
            return next === prev ? prev : next;
        });
    }, []);

    // Re-measure after every render so a state-driven row-count change
    // gets a second pass to confirm fit (or trim further).
    useLayoutEffect(() => {
        recomputeAdvisorCount();
    }, [recomputeAdvisorCount, visibleAdvisorCount, advisors.length]);

    // Observers for viewport / container resize. Re-attached only when
    // `recomputeAdvisorCount` identity changes (it's stable via
    // `useCallback`), so the RO churn doesn't happen on every render.
    useEffect(() => {
        const section = sectionRef.current;
        if (!section) return undefined;
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recomputeAdvisorCount) : null;
        if (ro) ro.observe(section);
        // Window resize covers viewport-height-only changes (devtools
        // dock, mobile chrome collapse) that don't tickle the section's
        // bounding box until a subsequent layout pass.
        window.addEventListener('resize', recomputeAdvisorCount);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener('resize', recomputeAdvisorCount);
        };
    }, [recomputeAdvisorCount]);

    const closeHost = () => {
        if (typeof onAfterAction === 'function') onAfterAction();
    };

    const handleStartNewChat = () => {
        const path = `/${APP_URL_CHAT}/${defaultAdvisorAlias}`;
        if (hasUnsavedChanges) {
            dispatch(setPendingNavigation(path));
            return;
        }
        navigate(path);
        closeHost();
    };

    const handleAdvisorTap = (alias) => {
        // HORO-10504: same lock-reason check as the catalog card
        // (MarketplaceActionButton) — anonymous character-access gate, plus
        // Zara's premium-subscriber gate, since pinning her here
        // (useAdvisorList.js) makes her reachable from this list for anyone,
        // not just users who already had access when she landed in their
        // recents.
        const lockReason = getAdvisorLockReason(alias, { isUser, isPremiumUser: isPremiumSubscriber(user), zaraTrialExhausted });
        if (lockReason === ADVISOR_LOCK_REASON.REGISTER) {
            // HORO-10599: Zara's REGISTER case opens her own modal instead of
            // the generic register flow — mirrors MarketplaceActionButton.
            if (alias === ZARA_ADVISOR_ALIAS) {
                dispatch(setShowZaraUpsellModal(true));
                closeHost();
                return;
            }
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(STORAGE_KEY_PENDING_ADVISOR_ALIAS, alias);
            }
            dispatch(toggleAuthModal({ trigger: true, step: 1, context: 'advisorLocked' }));
            return;
        }
        if (lockReason === ADVISOR_LOCK_REASON.SUBSCRIBE) {
            // HORO-10562: same generalization as MarketplaceActionButton —
            // Zara keeps her bespoke modal, every other now-locked advisor
            // opens the generic subscribe flow instead.
            //
            // HORO-10563: PaymentModal itself now branches on whether the
            // viewer is signed in — see its own comment for why.
            if (alias === ZARA_ADVISOR_ALIAS) {
                dispatch(setShowZaraUpsellModal(true));
            } else {
                dispatch(openPaymentModal({ source: 'advisorLocked', advisorAlias: alias }));
            }
            closeHost();
            return;
        }
        // HORO-10504: unlocked (premium) Zara, but this account has never
        // been through her one-time welcome screen — show that instead of
        // navigating straight into a chat.
        if (shouldShowZaraWelcome(alias, { isPremiumUser: isPremiumSubscriber(user), seenAdvisorIntros })) {
            dispatch(setShowZaraWelcomeModal(true));
            closeHost();
            return;
        }

        // Set-default-advisor write paths are temporarily disabled — the
        // feature ships separately. Keep the read path (the existing
        // user.defaultAdvisorAlias on the store) honored for navigation,
        // so users who already have a stored default keep getting it,
        // we just don't *update* it on every advisor tap anymore.
        // TODO: re-enable `dispatch(setDefaultAdvisor(alias))` when the
        // set-default-advisor feature ships.
        if (!isUser) pushRecentAdvisorAlias(alias);

        // HORO-10370: every left-menu advisor tap fires advisor_card_click.
        // advisorSwitch() no-ops when there's no prior advisor or the user
        // taps the one they're already chatting with — safe to fire on
        // every tap.
        const tapped = advisors.find((a) => a.alias === alias);
        const position = advisors.findIndex((a) => a.alias === alias);
        const tappedName = tapped?.name || alias;
        gaEvents.advisorCardClick(tappedName, position >= 0 ? position : 0, 'rail');
        gaEvents.advisorSwitch(tappedName);

        const path = `/${APP_URL_CHAT}/${alias}`;
        if (hasUnsavedChanges) {
            dispatch(setPendingNavigation(path));
            return;
        }
        navigate(path);
        closeHost();
    };

    const handleViewAllClick = () => {
        gaEvents.viewAllAdvisorsClick();
        closeHost();
    };

    // HORO-10360: sidebar promo row. A chart (or stored birth details) goes
    // straight to the chart page, which builds it on first open; otherwise —
    // no details, or anonymous — start the collect-then-gate flow.
    const handleBirthChartTap = () => {
        const canOpenPage = isUser && (hasBirthChartReport || !!user?.birthDate);
        if (canOpenPage) {
            const path = `/${APP_URL_PROFILE}/${APP_URL_PROFILE_BIRTH_CHART}`;
            if (hasUnsavedChanges) {
                dispatch(setPendingNavigation(path));
                return;
            }
            navigate(path, { state: { birthChartSurface: BIRTH_CHART_VIEW_SURFACE.SIDEBAR } });
            closeHost();
            return;
        }
        closeHost();
        dispatch(openBirthChartFlow({ source: FLOW_SOURCE.SIDEBAR }));
    };

    // HORO-10471: the nav is one of the two entry points into the fake-door
    // waitlist, so the click is part of the demand funnel we're measuring.
    const handleLiveReadingsTap = (e) => {
        if (hasUnsavedChanges) {
            e.preventDefault();
            dispatch(setPendingNavigation(`/${APP_URL_LIVE_READINGS}`));
            return;
        }
        gaEvents.liveReadingsNavClick();
        closeHost();
    };

    // Tagged contexts drive both the register modal's copy variant
    // (EntryStep.js) and the post-login navigation in App.js:
    //   - `chatHistory`: anon user tapped the "Chat History" menu item and
    //     was bounced into sign-up first. After auth, take them to
    //     /chat-history (the destination they originally asked for).
    //   - `memory`: same, for the "Memory" menu item — after auth, take them
    //     to /memory. Previously this incorrectly reused `chatHistory`
    //     (copy-pasted), which silently misrouted a fresh Memory signup's
    //     post-login redirect to /chat-history instead.
    //   - `login`: explicit "Login" button. After auth, take them to their
    //     most recent chat — unless they're already mid-chat as anon, in
    //     which case the carry-over flow claims that chat under their new
    //     account and they stay where they are.
    const handleAnonAuthRequired = (context) => {
        dispatch(toggleAuthModal({ trigger: true, step: 1, context }));
        closeHost();
    };

    const handleLoginTap = () => {
        // HORO-10509: goes straight to the "Welcome back" sign-in view
        // rather than the Entry/signup screen first — `context: 'login'`
        // is kept as-is since App.js reads it for post-login navigation.
        dispatch(toggleAuthModal({ trigger: true, step: 1, context: 'login', flow: 'sign-in' }));
        closeHost();
    };

    const handleLogoutTap = () => {
        dispatch(logOut());
        closeHost();
    };

    return (
        <div className={styles.menuContent}>
            <div className={styles.section}>
                <button type="button" className={styles.menuItem} onClick={handleStartNewChat}>
                    <PlusIcon className={styles.menuItemIcon} aria-hidden="true" />
                    <span className={styles.menuItemLabel}>Start a New Chat</span>
                </button>
                {isUser ? (
                    // Plain Link for logged-in users so React Router handles
                    // the navigation cleanly — `useNavigate` + `closeHost`
                    // raced on the chat-history path and required a second
                    // tap. Mirrors v1's UserMenu Item.js pattern.
                    <Link
                        to={`/${APP_URL_CHAT_HISTORY}`}
                        className={styles.menuItem}
                        onClick={(e) => {
                            if (hasUnsavedChanges) {
                                e.preventDefault();
                                dispatch(setPendingNavigation(`/${APP_URL_CHAT_HISTORY}`));
                            } else {
                                closeHost();
                            }
                        }}
                    >
                        <ClockIcon className={styles.menuItemIcon} aria-hidden="true" />
                        <span className={styles.menuItemLabel}>History</span>
                    </Link>
                ) : (
                    <button type="button" className={styles.menuItem} onClick={() => handleAnonAuthRequired('chatHistory')}>
                        <ClockIcon className={styles.menuItemIcon} aria-hidden="true" />
                        <span className={styles.menuItemLabel}>History</span>
                    </button>
                )}
                {MEMORY_ENABLED &&
                    !isRegionRestricted &&
                    (isUser ? (
                        <Link to={`/${APP_URL_MEMORY}`} className={styles.menuItem} onClick={closeHost}>
                            <DocumentIcon className={styles.menuItemIcon} aria-hidden="true" />
                            <span className={styles.menuItemLabel}>Memory</span>
                            {new Date() < MEMORY_NEW_BADGE_UNTIL && <span className={styles.newBadge}>New</span>}
                        </Link>
                    ) : (
                        <button type="button" className={styles.menuItem} onClick={() => handleAnonAuthRequired('memory')}>
                            <DocumentIcon className={styles.menuItemIcon} aria-hidden="true" />
                            <span className={styles.menuItemLabel}>Memory</span>
                            {new Date() < MEMORY_NEW_BADGE_UNTIL && <span className={styles.newBadge}>New</span>}
                        </button>
                    ))}
                {/*
                    HORO-10471: Live Readings waitlist (fake-door demand test).
                    Unlike Memory / Chat History this is a plain Link for
                    everyone — the waitlist is deliberately reachable while
                    signed out, so there's no auth bounce. Rendering the same
                    markup for both audiences is also what puts the "Soon"
                    badge on desktop, which the mobile-only Figma omitted.
                */}
                {birthChartEnabled && (
                    <button type="button" className={styles.menuItem} onClick={handleBirthChartTap}>
                        <BirthChartIcon className={styles.menuItemIcon} aria-hidden="true" />
                        <span className={styles.menuItemLabel}>{t('birthChart.menu')}</span>
                        {isBirthChartNew && <span className={styles.newBadge}>{t('menu.new')}</span>}
                    </button>
                )}
                {/*
                    HORO-10703: a plain Link for everyone. /plans is public and
                    its own CTAs handle the register-then-checkout bounce, so an
                    auth gate here would only block the people it is meant to
                    convert.
                */}
                <Link
                    to={`/${APP_URL_PLANS}`}
                    className={styles.menuItem}
                    onClick={(e) => {
                        if (hasUnsavedChanges) {
                            e.preventDefault();
                            dispatch(setPendingNavigation(`/${APP_URL_PLANS}`));
                            return;
                        }
                        gaEvents.plansNavClick(isUser);
                        closeHost();
                    }}
                >
                    <PlansIcon className={styles.menuItemIcon} aria-hidden="true" />
                    <span className={styles.menuItemLabel}>{t('menu.plans')}</span>
                </Link>
                {LIVE_READINGS_ENABLED && (
                    <Link to={`/${APP_URL_LIVE_READINGS}`} className={styles.menuItem} onClick={handleLiveReadingsTap}>
                        <VideoIcon className={styles.menuItemIcon} aria-hidden="true" />
                        <span className={styles.menuItemLabel}>Live Readings</span>
                        <span className={styles.soonBadge}>Soon</span>
                    </Link>
                )}
            </div>

            <div ref={sectionRef} className={classNames(styles.section, styles.advisorsSection)}>
                <h3 className={styles.sectionHeader}>Advisors</h3>
                <div ref={advisorListRef} className={styles.advisorList}>
                    {advisors.slice(0, visibleAdvisorCount).map((advisor) => (
                        <AdvisorRow key={advisor.alias} advisor={advisor} onTap={handleAdvisorTap} />
                    ))}
                </div>
                <Link
                    ref={viewAllRef}
                    to={`/${APP_URL_CATALOG}`}
                    className={styles.viewAll}
                    onClick={(e) => {
                        if (hasUnsavedChanges) {
                            e.preventDefault();
                            dispatch(setPendingNavigation(`/${APP_URL_CATALOG}`));
                        } else {
                            handleViewAllClick();
                        }
                    }}
                >
                    <span className={styles.viewAllFull}>
                        View all
                        <ChevronRightIcon className={styles.viewAllChevron} aria-hidden="true" />
                    </span>
                    <ChevronDownIcon className={classNames(styles.viewAllChevron, styles.viewAllCollapsed)} aria-hidden="true" />
                </Link>
            </div>

            <div className={styles.footer}>
                {isUser ? (
                    <>
                        <Link
                            to={`/${APP_URL_PROFILE}`}
                            className={styles.userPill}
                            onClick={(e) => {
                                if (hasUnsavedChanges) {
                                    e.preventDefault();
                                    dispatch(setPendingNavigation(`/${APP_URL_PROFILE}`));
                                } else {
                                    closeHost();
                                }
                            }}
                        >
                            <div className={styles.userAvatar} aria-hidden="true">
                                {(user?.firstName?.[0] || user?.email?.[0] || '?').toUpperCase()}
                            </div>
                            <span className={styles.userName}>{user?.firstName || user?.email || t('common.user', 'You')}</span>
                        </Link>
                        <button type="button" className={styles.logoutButton} onClick={handleLogoutTap} aria-label={t('buttons.logout')}>
                            <LogoutIcon />
                        </button>
                    </>
                ) : (
                    <button type="button" className={styles.loginButton} onClick={handleLoginTap} aria-label="Login">
                        <span className={styles.loginButtonText}>Login</span>
                        <LoginIcon className={styles.loginButtonIcon} aria-hidden="true" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default MenuContent;
